import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Newspaper, BookOpen, Scale, Loader2, AlertTriangle, ArrowRight, Home } from 'lucide-react';
import api from '@/lib/axios';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';

const TIPO_LABELS = { noticia: 'Notícia', guia: 'Guia', legislacao: 'Legislação', comunicado: 'Comunicado', opiniao: 'Opinião' };
const TIPO_COLORS = {
  noticia: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  guia: 'bg-sky-50 text-sky-700 border-sky-200',
  legislacao: 'bg-amber-50 text-amber-800 border-amber-200',
  comunicado: 'bg-slate-100 text-slate-700 border-slate-200',
  opiniao: 'bg-violet-50 text-violet-700 border-violet-200',
};

export function TipoBadge({ tipo, testId = 'news-type-badge' }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-bold ${TIPO_COLORS[tipo] || TIPO_COLORS.noticia}`} data-testid={testId}>
      {TIPO_LABELS[tipo] || 'Notícia'}
    </span>
  );
}

function ArticleCard({ a }) {
  return (
    <Link to={`/noticias-tvde/artigo/${a.slug}`} className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-emerald-300 hover:shadow-md" data-testid={`news-card-${a.slug}`}>
      <div className="flex items-center gap-2"><TipoBadge tipo={a.tipo} />{a.categoria && <span className="text-[11px] font-semibold text-slate-400">{a.categoria.replace(/-/g, ' ')}</span>}</div>
      <h3 className="mt-3 text-base font-extrabold leading-snug text-slate-900 group-hover:text-emerald-700">{a.titulo}</h3>
      {a.resumo && <p className="mt-2 line-clamp-3 text-sm text-slate-600">{a.resumo}</p>}
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-emerald-600">Ler <ArrowRight className="h-4 w-4" /></span>
    </Link>
  );
}

export default function NewsCenterPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [categoria, setCategoria] = useState('');
  const [tipo, setTipo] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, limit: 9 });
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = 'Centro de Informação TVDE | Marketplace TVDE';
    api.get('/marketplace/news/categories').then((r) => setCats(r.data.items || [])).catch(() => {});
    api.get('/marketplace/news/home-block').then((r) => setFeatured(r.data.featured)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await api.get('/marketplace/news/articles', { params: { q: q || undefined, categoria: categoria || undefined, tipo: tipo || undefined, page, limit: 9 } });
      setData(r.data);
    } catch (e) { setError(true); } finally { setLoading(false); }
  }, [q, categoria, tipo, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.limit || 9)));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-emerald-700" data-testid="news-home-link"><Home className="h-4 w-4" /> Marketplace TVDE</Link>
          <Link to="/noticias-tvde/legislacao" className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-800" data-testid="news-legislation-link"><Scale className="h-4 w-4" /> Legislação atual</Link>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="flex items-center gap-3">
          <Newspaper className="h-8 w-8 text-emerald-600" />
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 sm:text-4xl">Centro de Informação TVDE</h1>
            <p className="mt-1 text-base text-slate-600">Notícias, legislação e guias práticos para quem trabalha no TVDE.</p>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Pesquisar por título, resumo ou conteúdo…" className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-emerald-400" data-testid="news-search-input" />
          </div>
          <select value={categoria} onChange={(e) => { setPage(1); setCategoria(e.target.value); }} className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm" data-testid="news-filter-categoria">
            <option value="">Todos os temas</option>
            {cats.map((c) => <option key={c.slug} value={c.slug}>{c.nome}</option>)}
          </select>
          <select value={tipo} onChange={(e) => { setPage(1); setTipo(e.target.value); }} className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm" data-testid="news-filter-tipo">
            <option value="">Todos os tipos</option>
            {Object.entries(TIPO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        {featured && !q && !categoria && !tipo && page === 1 && (
          <Link to={`/noticias-tvde/artigo/${featured.slug}`} className="mt-6 block rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6 shadow-sm transition hover:shadow-md" data-testid="news-featured">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-bold text-white">Destaque editorial</span>
            <h2 className="mt-3 text-2xl font-extrabold text-slate-900">{featured.titulo}</h2>
            {featured.resumo && <p className="mt-2 max-w-3xl text-sm text-slate-600">{featured.resumo}</p>}
          </Link>
        )}

        <div className="mt-8">
          {loading ? (
            <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 text-slate-500" data-testid="news-loading"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> A carregar…</div>
          ) : error ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-10 text-center" data-testid="news-error">
              <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
              <p className="mt-3 font-bold text-amber-900">Não foi possível carregar os conteúdos. Pode ser um problema temporário de ligação.</p>
              <button onClick={load} className="mt-4 rounded-full bg-amber-500 px-5 py-2 text-sm font-bold text-white hover:bg-amber-600" data-testid="news-retry">Tentar novamente</button>
            </div>
          ) : data.items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="news-empty">
              <BookOpen className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 font-semibold text-slate-700">Ainda não há conteúdos publicados com estes critérios.</p>
              <p className="mt-1 text-sm text-slate-500">Experimente limpar a pesquisa ou os filtros.</p>
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="news-grid">
                {data.items.map((a) => <ArticleCard key={a.slug} a={a} />)}
              </div>
              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2" data-testid="news-pagination">
                  <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold disabled:opacity-40" data-testid="news-prev">Anterior</button>
                  <span className="text-sm text-slate-600">Página {page} de {totalPages}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold disabled:opacity-40" data-testid="news-next">Seguinte</button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          <Link to="/noticias-tvde/legislacao" className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 transition hover:shadow-md" data-testid="news-legislacao-cta">
            <Scale className="h-7 w-7 text-amber-600" />
            <div><p className="font-extrabold text-amber-900">Biblioteca legal TVDE</p><p className="text-sm text-amber-800">Legislação atual e histórico, com comparação “Antes e agora”.</p></div>
          </Link>
          <button onClick={() => { setTipo('guia'); setPage(1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-3 rounded-2xl border border-sky-200 bg-sky-50 p-5 text-left transition hover:shadow-md" data-testid="news-guias-cta">
            <BookOpen className="h-7 w-7 text-sky-600" />
            <div><p className="font-extrabold text-sky-900">Guias essenciais</p><p className="text-sm text-sky-800">Percursos práticos para começar e trabalhar no TVDE.</p></div>
          </button>
        </div>
      </div>
      <MarketplaceFooter />
    </div>
  );
}
